/**
 * Parallax Effect Module
 *
 * This module applies a parallax effect to elements with the `.parallax` class,
 * driven by the vertical scroll position.
 * It's performance-optimized, using IntersectionObserver to only animate elements
 * when they are in the viewport.
 *
 * Configuration is done via data attributes on the HTML element:
 * - `data-parallax-speed`: A number (e.g., 0.1 for a slow effect). The magnitude of the effect.
 * - `data-parallax-direction`: 'up' (default), 'down', 'left', or 'right'.
 *   Determines the axis and direction of movement relative to vertical scroll.
 *   A negative `data-parallax-speed` can also be used to invert the direction.
 */

import { getLenisInstance } from './scroll-manager.js';

const BREAKPOINTS = {
    xs: 680,
    md: 980,
};

// Use a Map to store configuration for active elements.
// This avoids repeated dataset reads in the animation loop and is more performant.
const parallaxConfigCache = new Map();
let activeParallaxElements = new Set();
let lenis;
let animationFrameId;
let observer;

/**
 * Gets the current breakpoint key ('xs', 'md', 'lg') based on window width.
 * @returns {string} The current breakpoint key.
 */
function getCurrentBreakpoint() {
    const width = window.innerWidth;
    if (width <= BREAKPOINTS.xs) return 'xs';
    if (width <= BREAKPOINTS.md) return 'md';
    return 'lg';
}

/**
 * Resolves the correct parallax value (speed or direction) for the current breakpoint.
 * It cascades from the smallest breakpoint up to the default (lg).
 * @param {object} config - The cached configuration object for the element.
 * @param {string} property - The property to resolve ('speed' or 'direction').
 * @param {string} breakpoint - The current breakpoint key ('xs', 'md', 'lg').
 * @returns {number|string} The resolved value.
 */
function getResponsiveValue(config, property, breakpoint) {
    // Start with the default value (lg)
    let value = config[property].lg;
    // Override with medium if it exists and screen is md or smaller
    if ((breakpoint === 'md' || breakpoint === 'xs') && config[property].md !== null) {
        value = config[property].md;
    }
    // Override with small if it exists and screen is xs
    if (breakpoint === 'xs' && config[property].xs !== null) {
        value = config[property].xs;
    }
    return value;
}

/**
 * The main animation loop. It runs only when there are active parallax elements.
 */
function updateParallax() {
    if (activeParallaxElements.size === 0) {
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }
        return;
    }

    const breakpoint = getCurrentBreakpoint(); // Determine breakpoint once per frame.

    activeParallaxElements.forEach(el => {
        // Retrieve pre-calculated configuration from the cache.
        const config = parallaxConfigCache.get(el);
        if (!config) return;

        const { parent } = config;
        const speed = getResponsiveValue(config, 'speed', breakpoint);
        const direction = getResponsiveValue(config, 'direction', breakpoint);

        // This is the only DOM read that needs to happen every frame.
        const parentRect = parent.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const parentCenter = parentRect.top + parentRect.height / 2;
        const distanceFromCenter = viewportHeight / 2 - parentCenter;

        let translateX = 0, translateY = 0;

        switch (direction) {
            case 'down':
                translateY = distanceFromCenter * speed * -1;
                break;
            case 'left':
                translateX = distanceFromCenter * speed * -1;
                break;
            case 'right':
                translateX = distanceFromCenter * speed;
                break;
            case 'up':
            default:
                translateY = distanceFromCenter * speed;
                break;
        }

        // Apply the transform. Using translate3d promotes hardware acceleration.
        el.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
        el.style.willChange = 'transform';
    });

    animationFrameId = requestAnimationFrame(updateParallax);
}

function startAnimationLoop() {
    if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(updateParallax);
    }
}

export function initializeParallax() {
    lenis = getLenisInstance();
    if (!lenis) {
        console.warn('Parallax: Lenis not initialized. Parallax effects will not work.');
        return;
    }

    const allParallaxElements = document.querySelectorAll('.parallax');
    if (allParallaxElements.length === 0) return;

    // Filter out elements whose direct parent is a <picture> element.
    // This is to avoid applying parallax to an <img> that is part of a <picture> set,
    // where the parallax should likely be on a containing element instead.
    const elements = Array.from(allParallaxElements).filter(
        el => el.parentElement?.tagName.toLowerCase() !== 'picture'
    );

    if (elements.length === 0) return;

    observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            const el = entry.target;
            if (entry.isIntersecting) {
                // Cache the configuration when the element becomes active.
                // This prevents reading from the DOM on every animation frame.
                parallaxConfigCache.set(el, {
                    parent: el.parentElement,
                    speed: {
                        lg: parseFloat(el.dataset.parallaxSpeed) || 0.1,
                        md: el.dataset.parallaxSpeedMd !== undefined ? parseFloat(el.dataset.parallaxSpeedMd) : null,
                        xs: el.dataset.parallaxSpeedXs !== undefined ? parseFloat(el.dataset.parallaxSpeedXs) : null,
                    },
                    direction: {
                        lg: el.dataset.parallaxDirection || 'up',
                        md: el.dataset.parallaxDirectionMd || null,
                        xs: el.dataset.parallaxDirectionXs || null,
                    }
                });

                // Hint to the browser that this element's transform will change.
                el.style.willChange = 'transform';
                activeParallaxElements.add(el);
                startAnimationLoop();
            } else {
                // Clean up when the element is no longer active.
                activeParallaxElements.delete(el);
                parallaxConfigCache.delete(el);

                // Remove the hint and reset the transform to its default state.
                el.style.willChange = 'auto';
                el.style.transform = 'translate3d(0, 0, 0)';
            }
        });
    }, { rootMargin: '100px 0px 100px 0px' });

    elements.forEach(el => observer.observe(el));
}

export function destroyParallax() {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    if (observer) {
        observer.disconnect();
    }
    activeParallaxElements.clear();
    parallaxConfigCache.clear();
    animationFrameId = null;
    observer = null;
}